'use client'

import { useCallback, useMemo, useState } from 'react'

import { useDashboardWorkspaceContext } from '../dashboardContext'
import { useChangeDashboardMutation, useWorkspaceList } from '../dashboardQuery'
import SpinnerCircle from '~/components/SpinnerCircle'
import { WorkspaceSelect } from './WorkspaceSelect'
import { WorkspacListQuery } from '~/gql/graphql'
import { cloneDeep } from 'lodash/fp'
import { useShowNewWorkspaceModal } from '../workspace/modals/NewWorkspaceModal/state'

type Workspace = WorkspacListQuery['getWorkspacesForMember'][number]
export type WorkspaceWithRelations = Workspace & {
  parent?: WorkspaceWithRelations
  children?: WorkspaceWithRelations[]
  selected?: boolean
}

function markSelectedRecursively(workspace: WorkspaceWithRelations) {
  workspace.selected = true
  if (workspace.parent) {
    markSelectedRecursively(workspace.parent)
  }
}

export function WorkspaceSwitch() {
  const [busy, setBusy] = useState(false)
  const dashboardWorkspace = useDashboardWorkspaceContext()
  const showNewWorkspaceModal = useShowNewWorkspaceModal()

  const changeDashboard = useChangeDashboardMutation()
  const response = useWorkspaceList(dashboardWorkspace.memberId || '')
  const memberWorkspaces = response?.data?.getWorkspacesForMember
  const selectedWorkspace = useMemo(() => {
    return memberWorkspaces?.find(
      (ws) => ws.id === dashboardWorkspace.workspace.id
    )
  }, [memberWorkspaces, dashboardWorkspace])

  const workspacesWithRelations = useMemo((): WorkspaceWithRelations[] => {
    // Start with a clone of workspaces received via the query
    const workspacesWithRelations: WorkspaceWithRelations[] = cloneDeep(
      memberWorkspaces ?? []
    )
    let selectedWorkspaceWithRelations

    // Create a map for efficient navigation to the parent
    const workspacesWithRelationsMap = new Map<string, WorkspaceWithRelations>()
    workspacesWithRelations.forEach((workspace) => {
      // Set children property
      workspace.children = []
      // Set selected workspace variable if it matches
      if (selectedWorkspace?.id === workspace.id) {
        selectedWorkspaceWithRelations = workspace
      }
      // Add to map
      workspacesWithRelationsMap.set(workspace.id, workspace)
    })

    // For each workspace, populate parent child relations
    workspacesWithRelations.forEach((workspace) => {
      const parent = !!workspace.parentId
        ? workspacesWithRelationsMap.get(workspace.parentId)
        : undefined
      if (parent) {
        workspace.parent = parent
        ;(parent.children as WorkspaceWithRelations[]).push(workspace)
      }
    })

    // Mark selected up the workspace hierarchy
    if (selectedWorkspaceWithRelations) {
      markSelectedRecursively(selectedWorkspaceWithRelations)
    }

    // Filter only the top level workspaces
    // i-e which have a parent value of undefined
    return workspacesWithRelations.filter(
      (workspace) => typeof workspace.parent === 'undefined'
    )
  }, [memberWorkspaces, selectedWorkspace])

  const handleSwitchWorkspace = useCallback(
    (workspace: WorkspaceWithRelations) => {
      if (workspace.id === selectedWorkspace?.id) {
        return
      }
      setBusy(true)
      changeDashboard.mutateAsync(workspace.id, {
        onSettled() {
          setBusy(false)
        },
      })
    },
    [selectedWorkspace]
  )

  const handleCreateWorkspace = useCallback((parentId?: string | null) => {
    showNewWorkspaceModal({
      parentId,
    })
  }, [])

  return response.status === 'loading' || busy ? (
    <SpinnerCircle />
  ) : (
    <>
      <WorkspaceSelect
        workspaces={workspacesWithRelations}
        onSelectItem={handleSwitchWorkspace}
        onCreateWorkspace={handleCreateWorkspace}
        selectedWorkspace={selectedWorkspace}
      />
    </>
  )
}
